import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { API_PATH } from 'src/environments/environment';
import { AuthService } from '../auth/shared/auth.service';

const API = `${API_PATH}settings`;
const ALFASUPAY_KEY = 'ALFASUPAY_CREDENTIALS';
const GOOGLE_MAPS_KEY = 'GOOGLE_MAPS';

export interface GoogleMapsSettings {
  enabled: boolean;
  apiKey: string;
}

export interface AlfaSuPayCredential {
  id: string;
  name: string;
  apiUrl: string;
  clientId: string;
  privateKey: string;
}

export interface SettingsBranch {
  id?: string;
  title: string;
  document: string;
  logo?: string;
  socialName?: string;
  email?: string;
  phone?: string;
  stateRegistration?: string;
  cep?: string;
  terminals?: number;
  users?: number;
  address?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  uf?: string;
  city?: string;
  country?: string;
  observation?: string;
  isActive?: boolean;
}

interface Setting {
  id?: string;
  key: string;
  value: string;
  type?: string;
  description?: string;
}

@Injectable({ providedIn: 'root' })
export class SettingsService {
  constructor(
    private http: HttpClient,
    private authService: AuthService,
  ) {}

  private getCompanySettingKey(baseKey: string): string {
    const user = this.authService.getCurrentUser() || {};
    const companyId = user?.partner?.id
      || user?.enterprise?.id
      || user?.tenant?.id
      || user?.tenantId
      || user?.id;
    return companyId ? `${baseKey}__COMPANY_${companyId}` : baseKey;
  }

  getAlfaSuPayCredentials(): Observable<AlfaSuPayCredential[]> {
    return this.http.get<Setting>(`${API}/${ALFASUPAY_KEY}`).pipe(
      map((s) => JSON.parse(s.value) as AlfaSuPayCredential[]),
      catchError(() => of([])),
    );
  }

  saveAlfaSuPayCredentials(credentials: AlfaSuPayCredential[]): Observable<Setting> {
    const value = JSON.stringify(credentials);
    return this.http.post<Setting>(API, {
      key: ALFASUPAY_KEY,
      value,
      type: 'integration',
      description: 'AlfaSuPay credentials list',
    });
  }


  getEmailTemplate(templateKey: string): Observable<{ title: string; message: string }> {
    return this.http.get<Setting>(`${API}/EMAIL_TEMPLATE_${templateKey}`).pipe(
      map((s) => JSON.parse(s.value) as { title: string; message: string }),
      catchError(() => of({ title: '', message: '' })),
    );
  }

  saveEmailTemplate(templateKey: string, data: { title: string; message: string }): Observable<Setting> {
    return this.http.post<Setting>(API, {
      key: `EMAIL_TEMPLATE_${templateKey}`,
      value: JSON.stringify(data),
      type: 'email-template',
      description: `Custom email template: ${templateKey}`,
    });
  }

  getNotificationTemplate(moduleKey: string): Observable<string> {
    return this.http.get<Setting>(`${API}/NOTIF_TEMPLATE_${moduleKey}`).pipe(
      map((s) => s.value as string),
      catchError(() => of('')),
    );
  }

  saveNotificationTemplate(moduleKey: string, message: string): Observable<Setting> {
    return this.http.post<Setting>(API, {
      key: `NOTIF_TEMPLATE_${moduleKey}`,
      value: message,
      type: 'notification-template',
      description: `Custom notification template: ${moduleKey}`,
    });
  }

  getChargeNotifPredefinedTemplates(): Observable<{ id: string; title: string; type: string; message: string }[]> {
    const key = this.getCompanySettingKey('CHARGE_NOTIF_PREDEFINED_TEMPLATES');
    return this.http.get<Setting>(`${API}/${key}`).pipe(
      map((s) => JSON.parse(s.value) as { id: string; title: string; type: string; message: string }[]),
      catchError(() => of([])),
    );
  }

  saveChargeNotifPredefinedTemplates(templates: { id: string; title: string; type: string; message: string }[]): Observable<Setting> {
    const key = this.getCompanySettingKey('CHARGE_NOTIF_PREDEFINED_TEMPLATES');
    return this.http.post<Setting>(API, {
      key,
      value: JSON.stringify(templates),
      type: 'notification-template',
      description: 'Predefined templates for Notificar Cliente modal',
    });
  }

  getServiceOrderNotifTemplates(channel: string): Observable<{ id: string; title: string; type: string; message: string }[]> {
    const key = this.getCompanySettingKey(`SERVICE_ORDER_NOTIF_TEMPLATES_${channel.toUpperCase()}`);
    return this.http.get<Setting>(`${API}/${key}`).pipe(
      map((s) => JSON.parse(s.value)),
      catchError(() => of([])),
    );
  }

  saveServiceOrderNotifTemplates(
    channel: string,
    templates: { id: string; title: string; type: string; message: string }[],
  ): Observable<Setting> {
    const key = this.getCompanySettingKey(`SERVICE_ORDER_NOTIF_TEMPLATES_${channel.toUpperCase()}`);
    return this.http.post<Setting>(API, {
      key,
      value: JSON.stringify(templates),
      type: 'notification-template',
      description: `Service order notification templates for ${channel}`,
    });
  }

  getChargePushPredefinedTemplates(): Observable<{ id: string; title: string; type: string; message: string }[]> {
    const key = this.getCompanySettingKey('CHARGE_PUSH_PREDEFINED_TEMPLATES');
    return this.http.get<Setting>(`${API}/${key}`).pipe(
      map((s) => JSON.parse(s.value) as { id: string; title: string; type: string; message: string }[]),
      catchError(() => of([])),
    );
  }

  saveChargePushPredefinedTemplates(templates: { id: string; title: string; type: string; message: string }[]): Observable<Setting> {
    const key = this.getCompanySettingKey('CHARGE_PUSH_PREDEFINED_TEMPLATES');
    return this.http.post<Setting>(API, {
      key,
      value: JSON.stringify(templates),
      type: 'notification-template',
      description: 'Predefined push notification templates for Cobrança Notif tab',
    });
  }

  uploadTemplateImage(file: File): Observable<{ key: string; url: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<{ key: string; url: string }>(`${API}/template-image`, formData);
  }

  getBranches(): Observable<SettingsBranch[]> {
    return this.http.get<SettingsBranch[]>(`${API}/branches`);
  }

  getGlobalActiveBranch(): Observable<SettingsBranch | null> {
    return this.http.get<SettingsBranch | null>(`${API}/branches/global-active`).pipe(
      map((branch) => branch || null),
      catchError(() => of(null)),
    );
  }

  getBranch(id: string): Observable<SettingsBranch> {
    return this.http.get<SettingsBranch>(`${API}/branches/${id}`);
  }

  createBranch(branch: SettingsBranch): Observable<SettingsBranch> {
    return this.http.post<SettingsBranch>(`${API}/branches`, branch);
  }

  updateBranch(id: string, branch: SettingsBranch): Observable<SettingsBranch> {
    return this.http.put<SettingsBranch>(`${API}/branches/${id}`, branch);
  }

  activateBranch(id: string): Observable<SettingsBranch> {
    return this.http.put<SettingsBranch>(`${API}/branches/${id}/activate`, {});
  }

  deleteBranch(id: string): Observable<void> {
    return this.http.delete<void>(`${API}/branches/${id}`);
  }

  getGoogleMaps(): Observable<GoogleMapsSettings> {
    return this.http.get<Setting>(`${API}/${GOOGLE_MAPS_KEY}`).pipe(
      map((setting) => {
        try {
          const parsed = JSON.parse(setting?.value || '{}') as GoogleMapsSettings;
          return {
            enabled: !!parsed.enabled,
            apiKey: parsed.apiKey || '',
          };
        } catch {
          return { enabled: false, apiKey: '' };
        }
      }),
      catchError(() => of({ enabled: false, apiKey: '' })),
    );
  }

  saveGoogleMaps(config: GoogleMapsSettings): Observable<Setting> {
    return this.http.post<Setting>(API, {
      key: GOOGLE_MAPS_KEY,
      value: JSON.stringify({
        enabled: !!config.enabled,
        apiKey: (config.apiKey || '').trim(),
      }),
      type: 'integration',
      description: 'Google Maps — mapa operacional e geocodificação',
    });
  }
}
